import { useEffect, useState, useSyncExternalStore } from 'react';
import { Button, Divider, Group, Modal, PasswordInput, Stack, Text, TextInput } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { PASSWORD_MIN } from '@parts/shared';
import { errorText, postJson } from '../api';
import { loadMe, logout, useAuth } from '../auth';
import { useI18n } from '../i18n';

/** Своё имя (видно в истории) и пароль. Окно одно на всё приложение, открывается из меню пользователя. */
let opened = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

export function openProfileDialog(): void {
  opened = true;
  notify();
}

function close(): void {
  opened = false;
  notify();
}

export function ProfileDialog() {
  const { t, tx } = useI18n();
  const isOpen = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => opened
  );
  const auth = useAuth();
  const [name, setName] = useState('');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setName(auth.user?.name ?? '');
    setCurrent('');
    setNext('');
    setRepeat('');
  }, [isOpen, auth.user?.name]);

  const saveName = async () => {
    setBusy(true);
    try {
      const res = await postJson<{ ok: boolean; message: string }>('/api/auth/profile', { name });
      notifications.show({ color: res.ok ? 'green' : 'red', message: tx(res.message) });
      if (res.ok) await loadMe();
    } catch (err) {
      notifications.show({ color: 'red', message: tx(errorText(err)) });
    } finally {
      setBusy(false);
    }
  };

  const savePassword = async () => {
    if (next !== repeat) {
      notifications.show({ color: 'red', message: t('Пароли не совпадают.') });
      return;
    }
    setBusy(true);
    try {
      const res = await postJson<{ ok: boolean; message: string }>('/api/auth/password', { current, next });
      notifications.show({ color: res.ok ? 'green' : 'red', message: tx(res.message) });
      if (res.ok) {
        close();
        await logout();
      }
    } catch (err) {
      notifications.show({ color: 'red', message: tx(errorText(err)) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={isOpen} onClose={close} title={t('Имя и пароль')} size="md">
      <Stack gap="md">
        <TextInput
          label={t('Имя')}
          description={t('Оно будет видно в истории действий.')}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={() => void saveName()} loading={busy} disabled={name.trim() === auth.user?.name}>
            {t('Сохранить имя')}
          </Button>
        </Group>
        <Divider />
        <Text size="sm" fw={500}>
          {t('Сменить пароль')}
        </Text>
        <PasswordInput label={t('Текущий пароль')} value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" />
        <PasswordInput
          label={t('Новый пароль')}
          description={t('Не короче {n} символов.', { n: PASSWORD_MIN })}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          autoComplete="new-password"
        />
        <PasswordInput label={t('Пароль ещё раз')} value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" />
        <Group justify="flex-end">
          <Button onClick={() => void savePassword()} loading={busy} disabled={!current || next.length < PASSWORD_MIN}>
            {t('Сменить пароль')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
